import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const orderedDitherPassSkill: CodeoSkill = {
    name: SKILL_NAMES.orderedDitherPass,
    kind: 'codeo',
    description:
        'Quantize a surface into a coarse ordered grid that resolves away — the dither pass, for ' +
        'an early-computing register or a state that arrives low-fidelity and sharpens.',
    documentUsesSkill: check(
        (script, markup) =>
            /background-size|backgroundSize/.test(`${markup}${script}`) &&
            /conic-gradient|repeating-linear-gradient|repeating-conic-gradient/.test(markup),
    ),
    instructions: `# Ordered Dither Pass

A surface appears as a coarse grid of hard-edged cells and then resolves into the real thing. It reads as low fidelity becoming high fidelity — an image loading in 1994, a signal locking, something being reconstructed — and it carries a specific early-computing register that a plain fade cannot.

The grid is a repeating conic or linear gradient used as a mask, tightened until the cells vanish. No canvas and no per-cell elements are required.

## Markup

The dithered layer sits over the resolved content and thins out as the pass completes. It is a **chequerboard, not a fill**: two quadrants of the tile paint in the stage's own background colour and hide what is beneath them, and the other two are genuinely transparent so the content shows through. That alternation is the whole effect — give the layer its own \`background-color\` and there is nothing to see through, so it renders as one solid block.

\`\`\`html
<div id="dither-stage" class="overflow-hidden" style="position: absolute; inset: 0; background: var(--l5-color-1, #0c0f16);">
  <div id="dither-content" class="flex items-center justify-center" style="position: absolute; inset: 0; font-family: var(--l5-font-0), sans-serif; font-size: 150px; font-weight: 800; color: var(--l5-color-2, #6ee7a8);">SIGNAL</div>
  <div id="dither-grid" style="position: absolute; inset: 0; background-image: conic-gradient(from 45deg, currentColor 0deg 90deg, transparent 90deg 180deg, currentColor 180deg 270deg, transparent 270deg 360deg); background-size: 22px 22px; background-position: center center; color: var(--l5-color-1, #0c0f16);"></div>
</div>
\`\`\`

## Timeline code

Two things resolve together: the cells get finer, and the whole layer fades out.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const gridEl = document.querySelector('#dither-grid');
const quantize = { cell: 22 };

tl.to(quantize, {
    cell: 4, // floors at 6 in onUpdate; the tail is carried by the fade
    duration: 1.2,
    ease: 'power2.inOut',
    onUpdate: () => {
        // Quantize to whole pixels and stop at a floor. A hard-edged tile re-laid at
        // fractional sizes aliases differently on every frame, which reads as flicker
        // rather than as resolving; below ~6px the cells are finer than the aliasing.
        const cell = Math.max(6, Math.round(quantize.cell));
        // Assign the pair in one expression. Building a "Npx Npx" string into a
        // variable first invites writing it twice, and four lengths is invalid.
        const px = \`\${cell}px\`;
        gridEl.style.backgroundSize = \`\${px} \${px}\`;
    },
}, 0.6);

tl.to('#dither-grid', { opacity: 0, duration: 0.7, ease: 'power2.in' }, 1.3);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Cell size**: 18–28px start is coarse enough to read as quantization at 1920 wide. Below about 8px the grid becomes texture rather than structure.
- **Scanlines instead**: a horizontal \`repeating-linear-gradient\` gives a CRT register rather than a dither one — flatter, more analogue.
- **Hold the grid**: stopping at 8px and keeping the layer leaves a permanent halftone-ish texture over the scene, which is a look rather than a transition.
- **Reverse**: coarsening rather than resolving reads as a signal degrading, which suits a problem statement.
- **Colour quantization**: giving the grid a palette colour rather than the background colour tints the cells and reads as a limited colour depth.
- **Print sibling**: for a dot-based rather than cell-based dissolve, use \`${SKILL_NAMES.halftoneDotDissolve}\`.

## Pitfalls

- Rebuild the whole \`backgroundSize\` string in \`onUpdate\`; GSAP cannot tween a shorthand with two length values on its own.
- **Quantize the cell to whole pixels and give it a floor.** Tweening the size continuously re-lays a hard-edged tile at fractional widths, so the pattern's phase shifts every frame and the pass reads as flicker instead of resolution. Round the value and stop around 6px — finer than that the cells are below the aliasing threshold anyway, and the fade should carry the rest.
- \`background-size\` takes **exactly two lengths per layer**, and layers are comma-separated. Four space-separated values is not a shorthand for two layers — it is invalid, so the browser drops the whole declaration silently and the cells never change size while the rest of the timeline carries on as if they had. Nothing lints it; the only symptom is a grid that fades out still coarse.
- Set \`background-position: center\`. A tiling background scales about its origin, which defaults to the top-left corner, so tightening the cells without it makes the whole grid appear to rush toward that corner instead of resolving evenly across the frame.
- **Never set \`background-color\` on the grid layer.** The gradient's transparent quadrants reveal whatever is painted beneath them *on the same element* before they reveal the content. Set a background colour there and the transparent halves show that colour instead — identical to the \`currentColor\` halves — so the layer becomes a uniform block, no grid appears at all, and the only visible event is it fading out. Colour belongs on \`color\` alone.
- The grid's own colour must match the stage background exactly, or the cells read as a coloured overlay rather than as missing information.
- Fade the layer out as well as tightening it. A grid tightened to 3px is still visible as a screen door and looks like a rendering artifact.
- Text under a coarse dither is unreadable. Let the pass resolve before the moment the copy has to be read.
- This is a strong period-specific look. In a clean modern design language it reads as a bug — commit the design language or leave it out.
- Repainting a full-frame repeating gradient every frame is the expensive part; don't stack it with a blur or a second full-frame effect.
- Don't run a dither pass and a glitch and a scanline together. One artifact vocabulary per graphic.`,
};
